import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Upload, Trophy, Image as ImageIcon, Users, ChevronRight, Star,
  CalendarClock, TrendingUp, ShieldAlert, Lock, EyeOff, CheckCircle2, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * /team/profile — "My Team" dashboard for the Team Owner.
 * Reads `/api/owner/my-team` — a single consolidated endpoint that returns
 * team + roster + matches + standings + stats for the authenticated owner.
 * Backend enforces the relationship (user.team_id -> team.id).
 */
export default function TeamProfile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/my-team");
      setData(data);
      setErr("");
    } catch (e) {
      const detail = e?.response?.data?.detail || formatApiError(e);
      setErr(detail);
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const team = data?.team;
  const roster = data?.roster || [];
  const stats = data?.stats || {};
  const matches = data?.matches || { upcoming: [], live: [], finished: [] };
  const position = data?.standings_position;
  const positionTotal = data?.standings_total;
  const groupPosition = data?.group_position;
  const groupTotal = data?.group_total || 0;
  const groupStandings = data?.group_standings || [];

  // iter87 · Live refresh — piggy-back on the public standings SSE stream
  // (`/api/public/events/stream`) so any score save fires an immediate
  // reload of `/owner/my-team`. Fallback to a 45s poll if the stream is
  // rejected by the browser (Safari private mode, etc.).
  useEffect(() => {
    let es = null;
    const iv = setInterval(load, 45_000);
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      es = new EventSource(`${base}/api/public/events/stream`);
      const on = () => load();
      es.addEventListener("match.finalized", on);
      es.addEventListener("match.set_scored", on);
      es.addEventListener("standings.updated", on);
    } catch {
      /* silent */
    }
    return () => {
      clearInterval(iv);
      try { es && es.close(); } catch { /* noop */ }
    };
  }, [load]);

  const uploadLogo = async (file) => {
    if (!file || !team?.id) return;
    if (!file.type.startsWith("image/")) return toast.error("Image only");
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/teams/${team.id}/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  // 1) Not linked to a team
  if (err) {
    return (
      <div className="p-8 md:p-12 max-w-3xl" data-testid="team-profile-error">
        <Card className="p-8 bg-red-950/30 border-red-800/40">
          <div className="flex items-start gap-4">
            <ShieldAlert className="w-8 h-8 text-red-400" />
            <div>
              <h1 className="font-anton text-3xl uppercase text-white mb-2">No team linked to your account</h1>
              <div className="text-red-200/90 text-sm">{err}</div>
              <div className="text-xs text-zinc-400 mt-4">
                Ask a League Admin to open <code className="text-[#F4B400]">/admin/team-owners</code>
                {" "}and link your login <code className="text-[#F4B400]">{user?.email}</code> to a team.
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-zinc-500" data-testid="team-profile-loading">Loading your team…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="team-profile">
      {/* Team header */}
      <div className="flex items-start gap-5 flex-wrap mb-8">
        <div className="w-24 h-24 rounded-2xl bg-black/40 border-2 border-white/10 flex items-center justify-center overflow-hidden">
          {team?.logo_url ? (
            <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" data-testid="team-logo-preview" />
          ) : (
            <ImageIcon className="w-10 h-10 text-white/30" />
          )}
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">My Team</div>
          <h1
            className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white leading-none mt-1"
            style={{ color: team?.brand_color || team?.primary_color || "#fff" }}
            data-testid="team-name"
          >
            {team?.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
            {team?.group && <Badge className="bg-[#F4B400]/20 text-[#F4B400] border-[#F4B400]/40" data-testid="team-group">Group {team.group}</Badge>}
            {team?.tier && <Badge className="bg-white/10 text-white/80 border-white/10">Tier: {team.tier}</Badge>}
            {typeof groupPosition === "number" && (
              <Badge
                className="bg-[#00A86B]/15 text-[#00A86B] border-[#00A86B]/30"
                data-testid="team-group-position"
              >
                Group #{groupPosition} of {groupTotal}
              </Badge>
            )}
            {typeof position === "number" && (
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30" data-testid="team-position">
                Overall #{position} of {positionTotal}
              </Badge>
            )}
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              data-testid="logo-file-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="border-white/20 text-white ml-2"
              data-testid="upload-logo-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Uploading…" : "Change Logo"}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats strip — Season 3: GD is the ranking metric so it's front-and-centre */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4"
        data-testid="team-stats-strip"
      >
        <Stat label="Played"     value={stats.played}   testid="stat-played" />
        <Stat label="Won"        value={stats.won}      tone="ok" testid="stat-won" />
        <Stat label="Lost"       value={stats.lost}     tone="warn" testid="stat-lost" />
        <Stat label="Points"     value={stats.points}   tone="ok" testid="stat-points" />
        <Stat label="Games For"  value={stats.for}      testid="stat-for" />
        <Stat label="Games Agst" value={stats.against}  testid="stat-against" />
        <Stat
          label="Game Diff"
          value={
            (stats.game_diff ?? 0) > 0
              ? `+${stats.game_diff}`
              : String(stats.game_diff ?? 0)
          }
          tone={
            (stats.game_diff ?? 0) > 0
              ? "ok"
              : (stats.game_diff ?? 0) < 0
              ? "warn"
              : undefined
          }
          testid="stat-gd"
        />
      </div>

      {/* Group mini-standings + overall standing tag */}
      <GroupStandingsCard
        rows={groupStandings}
        myTeamId={team?.id}
        groupLabel={team?.group}
        overallPosition={position}
        overallTotal={positionTotal}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roster */}
        <Card className="p-5 bg-[#0f1a15] border-white/10 lg:col-span-1" data-testid="team-roster">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#F4B400]" />
            <div className="font-anton text-xl uppercase text-white">Roster</div>
            <span className="text-xs text-zinc-500 ml-1">({roster.length})</span>
          </div>
          {roster.length === 0 ? (
            <div className="text-xs text-zinc-500">No players signed yet.</div>
          ) : (
            <ul className="space-y-2">
              {roster.map(p => (
                <li key={p.id} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5" data-testid={`roster-player-${p.id}`}>
                  <div className="flex items-center gap-2">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-black/30 border border-white/10" />
                    )}
                    <div>
                      <div className="text-white">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">
                        {p.category || "?"}
                        {typeof p.current_ranking === "number" ? ` · #${p.current_ranking}` : ""}
                        {typeof p.fip_ranking === "number" ? ` · FIP ${p.fip_ranking}` : ""}
                      </div>
                    </div>
                  </div>
                  {p.is_international && <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30">Intl</Badge>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Matches */}
        <Card className="p-5 bg-[#0f1a15] border-white/10 lg:col-span-2" data-testid="team-matches">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-5 h-5 text-[#F4B400]" />
            <div className="font-anton text-xl uppercase text-white">Matches</div>
          </div>
          {matches.live.length > 0 && (
            <>
              <SectionTitle label="Live now" tone="live" />
              <MatchList list={matches.live} myTeamId={team?.id} />
            </>
          )}
          <SectionTitle label="Upcoming" tone="next" />
          {matches.upcoming.length === 0 ? (
            <div className="text-xs text-zinc-500 mb-4">Nothing on the calendar yet.</div>
          ) : (
            <UpcomingTieList list={matches.upcoming} myTeamId={team?.id} />
          )}
          <SectionTitle label="Recent Results" tone="done" />
          {matches.finished.length === 0 ? (
            <div className="text-xs text-zinc-500">No completed matches yet.</div>
          ) : (
            <MatchList list={matches.finished.slice(-5).reverse()} myTeamId={team?.id} showScore />
          )}
        </Card>
      </div>

      {/* Quick links */}
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/owner" data-testid="link-command-center">
          <Button variant="outline" className="border-white/20 text-white">
            <Star className="w-4 h-4 mr-2" /> Auction Command Center <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        <Link to="/standings" data-testid="link-standings">
          <Button variant="outline" className="border-white/20 text-white">
            <TrendingUp className="w-4 h-4 mr-2" /> Full Standings <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        {team?.slug && (
          <Link to={`/team/${team.slug}`} data-testid="link-public-team">
            <Button variant="outline" className="border-white/20 text-white">
              <Trophy className="w-4 h-4 mr-2" /> Public Team Page <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-white";
  return (
    <div className="px-3 py-3 rounded-xl bg-[#0d1a13] border border-white/10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-anton mt-0.5 ${color}`}>{value ?? 0}</div>
    </div>
  );
}

/**
 * iter87 · Compact group standings + overall-position card. Reads the exact
 * same rows the main `/standings` page renders (backend guarantees identical
 * ordering), so this table never drifts from the source of truth. The
 * logged-in team's row is highlighted with an amber ring so owners spot
 * themselves instantly.
 */
function GroupStandingsCard({ rows, myTeamId, groupLabel, overallPosition, overallTotal }) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  return (
    <Card
      className="p-5 bg-[#0f1a15] border-white/10 mb-8"
      data-testid="team-group-standings"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#F4B400]" />
          <div className="font-anton text-xl uppercase text-white">
            Group {groupLabel || "?"} Standings
          </div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Live · ranked by Game Difference
          </span>
        </div>
        {typeof overallPosition === "number" && (
          <div
            className="text-[11px] uppercase tracking-widest text-zinc-400"
            data-testid="team-overall-standing-line"
          >
            Overall Tournament Standing:{" "}
            <span className="text-emerald-300 font-bold">
              #{overallPosition}
            </span>{" "}
            of {overallTotal} teams
          </div>
        )}
      </div>
      {!hasRows ? (
        <div className="text-xs text-zinc-500 py-3">
          Group standings will appear here once matches begin.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-group-standings-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-2 py-2 w-10">#</th>
                <th className="px-2 py-2">Team</th>
                <th className="px-2 py-2 w-12 text-center">P</th>
                <th className="px-2 py-2 w-12 text-center">W</th>
                <th className="px-2 py-2 w-12 text-center">L</th>
                <th className="px-2 py-2 w-14 text-center">GF</th>
                <th className="px-2 py-2 w-14 text-center">GA</th>
                <th className="px-2 py-2 w-16 text-right">GD</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const gd = r.game_diff ?? (r.for - r.against);
                const isMe = r.id === myTeamId;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-white/5 ${
                      isMe
                        ? "bg-[#F4B400]/10 ring-1 ring-inset ring-[#F4B400]/60"
                        : ""
                    }`}
                    data-testid={`group-row-${r.id}`}
                    data-is-me={isMe ? "true" : undefined}
                  >
                    <td className="px-2 py-2 font-anton text-lg text-zinc-300">
                      {i + 1}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-anton text-white"
                          style={{
                            background: r.color || "#16322a",
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                        >
                          {(r.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <span
                          className={`font-bold ${
                            isMe ? "text-[#F4B400]" : "text-white"
                          }`}
                        >
                          {r.name}
                          {isMe && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest text-[#F4B400]/80">
                              You
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center text-zinc-300">{r.played}</td>
                    <td className="px-2 py-2 text-center text-emerald-300 font-bold">{r.won}</td>
                    <td className="px-2 py-2 text-center text-zinc-500">{r.lost}</td>
                    <td className="px-2 py-2 text-center text-zinc-300">{r.for}</td>
                    <td className="px-2 py-2 text-center text-zinc-300">{r.against}</td>
                    <td
                      className={`px-2 py-2 text-right font-anton text-xl tabular-nums ${
                        gd > 0
                          ? "text-emerald-300"
                          : gd < 0
                          ? "text-red-300"
                          : "text-white"
                      }`}
                    >
                      {gd > 0 ? "+" : ""}
                      {gd}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SectionTitle({ label, tone }) {
  const dot = tone === "live" ? "bg-red-500 animate-pulse" : tone === "next" ? "bg-[#F4B400]" : "bg-zinc-500";
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 mt-4 mb-2">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />{label}
    </div>
  );
}

function UpcomingTieList({ list, myTeamId }) {
  // Group matches by tie_id so each opponent appears once with a SET SEEDING CTA.
  const [statusByTie, setStatusByTie] = useState({}); // tie_id -> {my_status, revealed, locked, opp_name}
  const grouped = React.useMemo(() => {
    const byTie = new Map();
    for (const m of list) {
      const key = m.tie_id || m.id;
      if (!byTie.has(key)) byTie.set(key, []);
      byTie.get(key).push(m);
    }
    // Sort each tie's matches by tenant-order seed 2 → 1 → 3 (default) via `seed`
    const order = [2, 1, 3];
    const rank = Object.fromEntries(order.map((s, i) => [s, i]));
    return Array.from(byTie.entries()).map(([tie_id, ms]) => {
      ms.sort((a, b) => (rank[a.seed] ?? 99) - (rank[b.seed] ?? 99));
      const first = ms[0];
      const isA = first.team_a_id === myTeamId;
      const oppName = (isA ? first.team_b?.name : first.team_a?.name) || (isA ? first.team_b_name : first.team_a_name) || "TBD";
      const myName = (isA ? first.team_a?.name : first.team_b?.name) || (isA ? first.team_a_name : first.team_b_name) || "You";
      const when = first.scheduled_at ? new Date(first.scheduled_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "TBD";
      const stage = (first.stage || "group").toLowerCase();
      return { tie_id, matches: ms, oppName, myName, when, stage, court: first.court, venue: first.venue };
    });
  }, [list, myTeamId]);

  // Fetch lineup status for each unique tie once.
  useEffect(() => {
    const missing = grouped.filter((t) => !statusByTie[t.tie_id]);
    if (missing.length === 0) return;
    Promise.all(missing.map(async (t) => {
      try {
        const { data } = await api.get(`/lineups/mine/${t.tie_id}`);
        return { tie_id: t.tie_id, status: { my_status: data.my_status, revealed: data.revealed, locked: data.locked } };
      } catch {
        return { tie_id: t.tie_id, status: { my_status: "PENDING", revealed: false, locked: false } };
      }
    })).then((rows) => {
      setStatusByTie((prev) => {
        const next = { ...prev };
        for (const r of rows) next[r.tie_id] = r.status;
        return next;
      });
    });
  }, [grouped]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2.5">
      {grouped.map((t) => {
        const s = statusByTie[t.tie_id] || {};
        const isKnockout = t.stage !== "group";
        return (
          <div key={t.tie_id} className="rounded-lg border border-white/10 bg-[#11241b] p-3" data-testid={`upcoming-tie-${t.tie_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-anton text-lg text-white uppercase tracking-tight leading-tight">
                  {t.myName} <span className="text-zinc-500 text-sm mx-1">vs</span> {t.oppName}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {(t.venue ? `${t.venue} · ` : "") + (t.court || "TBD") + " · " + t.when + " · " + (isKnockout ? t.stage.toUpperCase() : "GROUP")}
                </div>
                {/* Seed row summary */}
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                  {t.matches.map((m, i) => (
                    <span key={m.id} className="px-2 py-0.5 rounded bg-black/40 border border-white/10 text-zinc-400">
                      <span className="text-[#F4B400] font-bold">M{i + 1}</span>
                      {m.seed ? <> · Seed <b className="text-white">{m.seed}</b></> : null}
                      {m.court && <span className="text-zinc-600 ml-1">· {m.court}</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="shrink-0 text-right">
                {s.locked ? (
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1 mb-1" data-testid={`tie-status-${t.tie_id}-locked`}>
                    <Lock className="w-3 h-3" /> Revealed · Locked
                  </div>
                ) : s.my_status === "SUBMITTED" ? (
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-1 mb-1" data-testid={`tie-status-${t.tie_id}-submitted`}>
                    <EyeOff className="w-3 h-3" /> Seeding Submitted · Hidden
                  </div>
                ) : null}
                <Link to={`/owner/lineups/${t.tie_id}`} data-testid={`set-seeding-btn-${t.tie_id}`}>
                  <Button size="sm" className={`font-bold uppercase tracking-widest text-[10px] ${s.locked ? "bg-emerald-500 text-black hover:bg-emerald-400" : s.my_status === "SUBMITTED" ? "border border-[#F4B400]/40 bg-transparent text-[#F4B400] hover:bg-[#F4B400]/10" : "bg-[#F4B400] text-black hover:bg-[#e5aa00]"}`}>
                    {s.locked ? (<><CheckCircle2 className="w-3 h-3 mr-1" />View Lineup</>) :
                     s.my_status === "SUBMITTED" ? (<><ClipboardList className="w-3 h-3 mr-1" />Edit Seeding</>) :
                     (<><ClipboardList className="w-3 h-3 mr-1" />Set Seeding</>)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MatchList({ list, myTeamId, showScore }) {
  return (
    <div className="space-y-1.5">
      {list.map(m => {
        const isA = m.team_a_id === myTeamId;
        const myName = (isA ? m.team_a?.name : m.team_b?.name) || (isA ? m.team_a_name : m.team_b_name) || "You";
        const oppName = (isA ? m.team_b?.name : m.team_a?.name) || (isA ? m.team_b_name : m.team_a_name) || "TBD";
        const when = m.scheduled_at ? new Date(m.scheduled_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "unscheduled";
        return (
          <div key={m.id} className="flex items-center justify-between text-sm bg-[#11241b] px-3 py-2 rounded-lg border border-white/5" data-testid={`match-${m.id}`}>
            <div className="text-white">
              <b>{myName}</b>
              <span className="text-zinc-500 mx-2">vs</span>
              <b>{oppName}</b>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {m.venue ? `${m.venue} · ` : ""}{m.court || "TBD"} · {when} · {m.stage || "group"}{m.seed ? ` · Seed ${m.seed}` : ""}
              </div>
            </div>
            {showScore && (
              <div className="text-right text-white font-mono text-lg">
                {m.score_a ?? 0} <span className="text-zinc-500 text-sm">—</span> {m.score_b ?? 0}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
